/**
 * WeatherSDK.js - 基于 OpenWeatherMap 和 CesiumWind 的气象图层封装 SDK
 * 提供温度、降水、云层、风速底图以及动态风场粒子流的加载与关闭功能
 */
//https://openweathermap.org/api/weather-map-1h?collection=maps
class WeatherSDK {
    /**
     * @param {Cesium.Viewer} viewer Cesium 视图实例
     * @param {Object} options 配置项
     * @param {string} options.apiKey OpenWeatherMap API Key
     */
    constructor(viewer, options = {}) {
        if (!viewer) throw new Error("Cesium Viewer instance is required.");
        if (!options.apiKey) throw new Error("OpenWeatherMap API Key is required.");

        this.viewer = viewer;
        this.apiKey = options.apiKey;
        this.layers = {
            temp: { name: 'temp_new', instance: null },
            precipitation: { name: 'precipitation_new', instance: null },
            clouds: { name: 'clouds_new', instance: null },
            pressure: { name: 'pressure_new', instance: null },
            wind: { name: 'wind_new', instance: null }
        };
        this.windParticleLayer = null;
        this.tileLoadCallback = null;
        this.legendContainer = null;
        this.loadingTipContainer = null;
        this.showLoadingProgress = false; // 是否需要显示进度提示的开关

        // 图例配置
        this.legendConfigs = {
            temp: {
                title: '温度 (°C)',
                icon: '🌡️',
                unit: '°C',
                gradient: 'linear-gradient(to right, #2468b4, #3c9dc2, #80cdc1, #97daa8, #c6e7b5, #eef7d9, #ffee9f, #fcd97d, #ffb664, #fc964b, #fa7034, #f54020, #ed2d1c, #dc1820, #b40023)',
                stops: [-40, -20, 0, 10, 20, 30, 40]
            },
            precipitation: {
                title: '降水 (mm/h)',
                icon: '💧',
                unit: 'mm/h',
                gradient: 'linear-gradient(to right, rgba(225, 200, 100, 0), rgba(200, 150, 150, 0), rgba(150, 150, 170, 0), rgba(120, 120, 190, 0), rgba(110, 110, 205, 0.3), rgba(80, 80, 225, 0.7), rgba(20, 20, 255, 0.9))',
                stops: [0, 0.5, 2, 5, 10, 30, 100]
            },
            clouds: {
                title: '云量 (%)',
                icon: '☁️',
                unit: '%',
                gradient: 'linear-gradient(to right, rgba(255,255,255,0), rgba(253,253,255,0.1), rgba(252,252,255,0.2), rgba(250,250,255,0.4), rgba(245,245,255,0.6), rgba(240,240,255,0.8), rgba(225,225,255,1))',
                stops: [0, 10, 25, 50, 75, 90, 100]
            },
            pressure: {
                title: '气压 (hPa)',
                icon: '⏲️',
                unit: 'hPa',
                gradient: 'linear-gradient(to right, #000033, #0000ff, #00ffff, #00ff00, #ffff00, #ff0000, #990000)',
                stops: [950, 980, 1000, 1013, 1025, 1040, 1050]
            },
            wind: {
                title: '风速 (m/s)',
                icon: '🌬️',
                unit: 'm/s',
                gradient: 'linear-gradient(to right, rgba(0,0,255,0.1), rgba(0,255,255,0.3), rgba(0,255,0,0.5), rgba(255,255,0,0.7), rgba(255,0,0,0.9))',
                stops: [0, 5, 10, 20, 30, 50, 100]
            }
        };

        this._injectStyles();

        // 自动绑定内部加载进度提示
        this.viewer.scene.globe.tileLoadProgressEvent.addEventListener((len) => {
            this._updateLoadingTip(len);
        });
    }

    /**
     * 注入图例样式
     * @private
     */
    _injectStyles() {
        if (document.getElementById('weather-sdk-styles')) return;
        const style = document.createElement('style');
        style.id = 'weather-sdk-styles';
        style.innerHTML = `
            /* 图例容器样式 */
            .weather-legend-container {
                position: absolute;
                bottom: 40px;
                right: 50px;
                background: linear-gradient(135deg, rgba(20, 20, 20, 0.95), rgba(40, 40, 40, 0.85));
                backdrop-filter: blur(12px);
                border: 1px solid rgba(255, 255, 255, 0.15);
                border-radius: 12px;
                padding: 16px;
                color: white;
                font-family: "Segoe UI", "Microsoft YaHei", sans-serif;
                box-shadow: 0 10px 30px rgba(0,0,0,0.6);
                z-index: 3000;
                width: 320px;
                transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                pointer-events: auto;
                user-select: none;
                display: none;
                flex-direction: column;
                gap: 12px;
            }
            .weather-legend-title {
                font-size: 14px;
                font-weight: 600;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                color: #ffffff;
                text-shadow: 0 2px 4px rgba(0,0,0,0.5);
            }
            .weather-legend-content {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }
            .weather-legend-bar {
                height: 14px;
                width: 100%;
                border-radius: 7px;
                border: 1px solid rgba(255,255,255,0.2);
                box-shadow: inset 0 1px 3px rgba(0,0,0,0.3);
            }
            .weather-legend-labels {
                display: flex;
                justify-content: space-between;
                font-size: 11px;
                font-weight: 500;
                color: #cccccc;
                padding: 0 2px;
            }
            .weather-legend-labels span {
                text-align: center;
                min-width: 20px;
            }

            /* 加载进度提示样式 */
            .weather-loading-tip {
                position: absolute;
                top: 45%;
                left: 50%;
                transform: translateX(-50%);
                background: rgba(10, 10, 10, 0.9);
                color: #fff;
                padding: 10px 20px;
                border-radius: 25px;
                font-size: 13px;
                z-index: 3500;
                display: none;
                align-items: center;
                gap: 10px;
                border: 1px solid rgba(255, 255, 255, 0.2);
                backdrop-filter: blur(8px);
                box-shadow: 0 4px 15px rgba(0,0,0,0.4);
            }
            .weather-loading-tip .spinner {
                width: 12px;
                height: 12px;
                border: 2px solid #fff;
                border-top-color: transparent;
                border-radius: 50%;
                animation: sdk-spin 0.8s linear infinite;
            }
            @keyframes sdk-spin {
                to { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * 更新图例展示
     * @param {string} type 
     * @private
     */
    _updateLegend(type) {
        const config = this.legendConfigs[type];
        if (!config) {
            this._removeLegend();
            return;
        }

        if (!this.legendContainer) {
            this.legendContainer = document.createElement('div');
            this.legendContainer.className = 'weather-legend-container';
            document.body.appendChild(this.legendContainer);
        }

        this.legendContainer.innerHTML = `
            <div class="weather-legend-title">
                <span>${config.icon}</span> ${config.title}
            </div>
            <div class="weather-legend-content">
                <div class="weather-legend-bar" style="background: ${config.gradient}"></div>
                <div class="weather-legend-labels">
                    ${config.stops.map(stop => `<span>${stop}</span>`).join('')}
                </div>
            </div>
        `;
        this.legendContainer.style.display = 'flex';
    }

    /**
     * 移除图例
     * @private
     */
    _removeLegend() {
        if (this.legendContainer) {
            this.legendContainer.style.display = 'none';
        }
    }

    /**
     * 更新加载进度提示
     * @param {number} queueLength 
     * @private
     */
    _updateLoadingTip(queueLength) {
        // 如果开关关闭，直接返回，不再处理进度提示
        if (!this.showLoadingProgress) return;

        if (!this.loadingTipContainer) {
            this.loadingTipContainer = document.createElement('div');
            this.loadingTipContainer.className = 'weather-loading-tip';
            this.loadingTipContainer.innerHTML = `
                <div class="spinner"></div>
                <span class="text">数据加载中...</span>
            `;
            document.body.appendChild(this.loadingTipContainer);
        }

        if (this._loadingTimeout) clearTimeout(this._loadingTimeout);

        const textEl = this.loadingTipContainer.querySelector('.text');
        if (queueLength > 20) {
            this.loadingTipContainer.style.display = 'flex';
            textEl.innerText = `正在请求气象数据...`;
        } else {
            textEl.innerText = `加载完成`;

            // 关键：一旦初次加载完成，关闭开关，后续地图操作将不再触发提示
            this.showLoadingProgress = false;

            this._loadingTimeout = setTimeout(() => {
                if (this.loadingTipContainer) {
                    this.loadingTipContainer.style.display = 'none';
                }
            }, 800);
        }
    }

    /**
     * 设置瓦片加载进度的回调函数
     * @param {Function} callback 
     */
    onProgress(callback) {
        this.tileLoadCallback = callback;
        this.viewer.scene.globe.tileLoadProgressEvent.addEventListener(this.tileLoadCallback);
    }

    /**
     * 加载气象底图层
     * @param {string} type 'temp' | 'precipitation' | 'clouds' | 'pressure' | 'wind'
     * @param {Object} options { alpha: 0.65, maxLevel: 5 }
     */
    async loadWeatherLayer(type, options = {}) {
        const config = this.layers[type];
        if (!config) throw new Error(`Unknown layer type: ${type}`);

        // 开启进度提示开关
        this.showLoadingProgress = true;

        // 如果该图层已存在，先移除
        this.removeWeatherLayer(type);

        const alpha = options.alpha !== undefined ? options.alpha : 0.65;
        const maxLevel = options.maxLevel !== undefined ? options.maxLevel : 5;

        try {
            const provider = new Cesium.UrlTemplateImageryProvider({
                url: `https://tile.openweathermap.org/map/${config.name}/{z}/{x}/{y}.png?appid=${this.apiKey}`,
                minimumLevel: 0,
                maximumLevel: maxLevel,
                credit: new Cesium.Credit('OpenWeatherMap')
            });

            // 等待 readyPromise (兼容旧版 Cesium)
            if (provider.readyPromise) {
                await provider.readyPromise;
            }

            const layer = this.viewer.imageryLayers.addImageryProvider(provider);
            layer.alpha = alpha;
            config.instance = layer;

            // 更新图例
            this._updateLegend(type);

            console.log(`[WeatherSDK] Layer loaded: ${type}`);
            return layer;
        } catch (error) {
            console.error(`[WeatherSDK] Failed to load layer ${type}:`, error);
            throw error;
        }
    }

    /**
     * 移除特定气象图层
     * @param {string} type 
     */
    removeWeatherLayer(type) {
        const config = this.layers[type];
        if (config && config.instance) {
            this.viewer.imageryLayers.remove(config.instance);
            config.instance = null;
            this._removeLegend();
            console.log(`[WeatherSDK] Layer removed: ${type}`);
        }
    }

    /**
     -> 移除所有气象底图层
     */
    removeAllWeatherLayers() {
        Object.keys(this.layers).forEach(type => this.removeWeatherLayer(type));
    }

    /**
     * 切换动态风场粒子流
     * @param {boolean} active 是否开启
     * @param {Object} options 粒子流配置
     */
    async toggleWindParticle(active, options = {}) {
        if (!active) {
            if (this.windParticleLayer) {
                this.windParticleLayer.remove();
                this.windParticleLayer = null;
                console.log("[WeatherSDK] Wind particle layer removed.");
            }
            return;
        }

        if (this.windParticleLayer) return;

        const sources = options.sources || [
            './2024122600.json',
            './fc.json',
            'https://fastly.jsdelivr.net/gh/sakitam-fdd/wind-layer@master/packages/plugins/cesium/examples/data/wind.json'
        ];

        let data = null;
        for (const url of sources) {
            try {
                const response = await fetch(url);
                if (!response.ok) continue;
                const text = await response.text();
                if (text.includes("Package size too large")) continue;
                data = JSON.parse(text);
                break;
            } catch (e) {
                continue;
            }
        }

        if (!data) throw new Error("Could not load wind data from any source.");

        const windOptions = options.windOptions || {
            colorScale: [
                'rgb(36,104, 180)', 'rgb(60,157, 194)', 'rgb(128,205,193 )',
                'rgb(151,218,168 )', 'rgb(198,231,181)', 'rgb(238,247,217)',
                'rgb(255,238,159)', 'rgb(252,217,125)', 'rgb(255,182,100)',
                'rgb(252,150,75)', 'rgb(250,112,52)', 'rgb(245,64,32)',
                'rgb(237,45,28)', 'rgb(220,24,32)', 'rgb(180,0,35)'
            ],
            frameRate: 16,
            maxAge: 60,
            globalAlpha: 0.85,
            velocityScale: 1 / 30,
            paths: 2500
        };

        if (typeof CesiumWind === 'undefined') {
            throw new Error("cesium-wind.js is required for particle effects.");
        }

        this.windParticleLayer = new CesiumWind.WindLayer(data, { windOptions });
        this.windParticleLayer.addTo(this.viewer);
        console.log("[WeatherSDK] Wind particle layer added.");
    }

    /**
     * 测试 API 连接性
     */
    async testConnection() {
        try {
            const response = await fetch(
                `https://tile.openweathermap.org/map/temp_new/0/0/0.png?appid=${this.apiKey}`,
                { method: 'HEAD' }
            );
            return response.ok;
        } catch (e) {
            return false;
        }
    }

    /**
     * 销毁 SDK 实例
     */
    destroy() {
        this.removeAllWeatherLayers();
        if (this.windParticleLayer) {
            this.windParticleLayer.remove();
        }
        if (this.tileLoadCallback) {
            this.viewer.scene.globe.tileLoadProgressEvent.removeEventListener(this.tileLoadCallback);
        }
    }
}

export default WeatherSDK;
// 导出 SDK
window.WeatherSDK = WeatherSDK;
